import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { VolunteersExperienceService } from "../../services/volunteersExperienceService";
import { VolunteersExperienceValidator } from "../../validators/volunteersExperienceValidator";
import { getResponseErrorMessage, isNotFoundError, showRequestError } from "../../utils/cmsResponse";
import { toCrLf } from "../../utils/lineBreaks";
import { isPictureValid } from "../../utils/pictureFile";
import { OperationStatus } from "../VolunteersHighlightContext/constants";
import {
	getDefaultVolunteersExperienceFormData,
	toVolunteersExperienceEditFormData,
	UpdateVolunteersExperienceLocalizable,
	UpdateVolunteersExperienceRequest,
	VOLUNTEERS_EXPERIENCE_LIST_PATH,
	VolunteersExperienceDetail,
	VolunteersExperienceEditFormData,
	VolunteersExperienceFormData,
	VolunteersExperienceListItem,
} from "./constants";

const NOT_FOUND_MESSAGE = "Gönüllü deneyimi bulunamadı.";
// A renamed or broken file passes the type and size checks, and the backend would refuse it with an unreadable message.
const INVALID_PICTURE_MESSAGE = "Fotoğraf okunamadı ya da içeriği dosya uzantısıyla uyuşmuyor. Lütfen geçerli bir JPEG, PNG veya GIF görseli seçin.";

interface VolunteersExperienceContextState {
	// Core Domain Data
	volunteersExperienceList: VolunteersExperienceListItem[];
	volunteersExperienceDetail: VolunteersExperienceDetail | null;
	volunteersExperienceForm: VolunteersExperienceFormData;
	volunteersExperienceEditForm: VolunteersExperienceEditFormData | null;
	operationStatus: OperationStatus;

	// Setters
	setVolunteersExperienceForm: React.Dispatch<React.SetStateAction<VolunteersExperienceFormData>>;
	setVolunteersExperienceEditForm: React.Dispatch<React.SetStateAction<VolunteersExperienceEditFormData | null>>;
	clearVolunteersExperienceFormData: () => void;
	clearVolunteersExperienceList: () => void;

	// Actions
	getVolunteersExperienceList: () => Promise<void>;
	getVolunteersExperienceDetails: (id: string) => Promise<void>;
	addVolunteersExperience: () => Promise<void>;
	updateVolunteersExperience: () => Promise<void>;
	removeVolunteersExperience: (id: string) => Promise<void>;
}

interface VolunteersExperienceProviderProps {
	children: React.ReactNode;
}

const VolunteersExperienceContext = createContext<VolunteersExperienceContextState | undefined>(undefined);

export const useVolunteersExperienceContext = () => {
	const context = useContext(VolunteersExperienceContext);
	if (!context) {
		throw new Error("useVolunteersExperienceContext must be used within a VolunteersExperienceContextProvider");
	}
	return context;
};

const showError = (error: unknown) => showRequestError(error, NOT_FOUND_MESSAGE);

/* -------------- Build Update Request (Only Changed Fields) -------------- */

// Line breaks are compared the way they are sent.
const isTextChanged = (current: string, original: string) => toCrLf(current.trim()) !== toCrLf(original.trim());

export const buildVolunteersExperienceUpdateRequest = (
	current: VolunteersExperienceEditFormData,
	original: VolunteersExperienceEditFormData
): UpdateVolunteersExperienceRequest => {
	const request: UpdateVolunteersExperienceRequest = { experienceId: current.experienceId };

	if (current.isActive !== original.isActive) {
		request.isActive = current.isActive;
	}
	if (current.displayOrder !== original.displayOrder) {
		request.displayOrder = current.displayOrder ?? null;
	}
	if (isTextChanged(current.name, original.name)) {
		request.name = current.name;
	}
	if (isTextChanged(current.surname, original.surname)) {
		request.surname = current.surname;
	}
	if (current.picture) {
		request.picture = current.picture;
	}

	const changedLocalizables = current.volunteersExperienceLocalizable.flatMap(item => {
		const before = original.volunteersExperienceLocalizable.find(o => o.culture === item.culture);
		const changed: UpdateVolunteersExperienceLocalizable = { culture: item.culture };
		if (item.localizableId) {
			changed.volunteersExperienceLocalizableId = item.localizableId;
		}
		let hasChange = false;

		for (const field of ["description", "title", "company"] as const) {
			if (isTextChanged(item[field], before?.[field] ?? "")) {
				changed[field] = item[field];
				hasChange = true;
			}
		}

		return hasChange ? [changed] : [];
	});

	if (changedLocalizables.length > 0) {
		request.volunteersExperienceLocalizable = changedLocalizables;
	}

	return request;
};

export const VolunteersExperienceContextProvider: React.FC<VolunteersExperienceProviderProps> = ({ children }) => {
	const navigate = useNavigate();
	const [volunteersExperienceList, setVolunteersExperienceList] = useState<VolunteersExperienceListItem[]>([]);
	const [volunteersExperienceDetail, setVolunteersExperienceDetail] = useState<VolunteersExperienceDetail | null>(null);
	const [volunteersExperienceForm, setVolunteersExperienceForm] = useState<VolunteersExperienceFormData>(getDefaultVolunteersExperienceFormData);
	const [volunteersExperienceEditForm, setVolunteersExperienceEditForm] = useState<VolunteersExperienceEditFormData | null>(null);
	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });
	// The loading state only disables the buttons after a re-render, so a fast double click could still send a
	// second request; this ref blocks overlapping add/update/delete calls synchronously.
	const isMutatingRef = useRef(false);

	/* -------------- Clear helpers -------------- */

	const clearVolunteersExperienceFormData = useCallback(() => {
		setVolunteersExperienceForm(getDefaultVolunteersExperienceFormData());
		setVolunteersExperienceEditForm(null);
		setVolunteersExperienceDetail(null);
	}, []);

	const clearVolunteersExperienceList = useCallback(() => {
		setVolunteersExperienceList([]);
	}, []);

	/* -------------- Actions -------------- */

	const getVolunteersExperienceList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await VolunteersExperienceService.getVolunteersExperienceList();

			if (response.isSuccessful) {
				setVolunteersExperienceList(response.result?.items ?? []);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const getVolunteersExperienceDetails = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await VolunteersExperienceService.getVolunteersExperienceDetails(id);

			if (response.isSuccessful) {
				setVolunteersExperienceDetail(response.result);
				setVolunteersExperienceEditForm(toVolunteersExperienceEditFormData(response.result));
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
			if (isNotFoundError(error)) {
				navigate(VOLUNTEERS_EXPERIENCE_LIST_PATH);
			}
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [navigate]);

	const addVolunteersExperience = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}

		const validationError = VolunteersExperienceValidator.validateAddVolunteersExperience(volunteersExperienceForm);
		if (validationError) {
			message.info(validationError);
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "add" });

		try {
			if (!(await isPictureValid(volunteersExperienceForm.picture))) {
				message.info(INVALID_PICTURE_MESSAGE);
				return;
			}

			const response = await VolunteersExperienceService.addVolunteersExperience(volunteersExperienceForm);

			if (response.isSuccessful) {
				message.success("Gönüllü deneyimi başarıyla eklendi.");
				setVolunteersExperienceForm(getDefaultVolunteersExperienceFormData());
				navigate(VOLUNTEERS_EXPERIENCE_LIST_PATH);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersExperienceForm, navigate]);

	const updateVolunteersExperience = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}
		if (!volunteersExperienceEditForm || !volunteersExperienceDetail) {
			message.error(NOT_FOUND_MESSAGE);
			return;
		}

		const updateRequest = buildVolunteersExperienceUpdateRequest(
			volunteersExperienceEditForm,
			toVolunteersExperienceEditFormData(volunteersExperienceDetail)
		);

		const validationError = VolunteersExperienceValidator.validateUpdateVolunteersExperience(updateRequest);
		if (validationError) {
			message.info(validationError);
			return;
		}

		if (Object.keys(updateRequest).length === 1) {
			message.info("Herhangi bir değişiklik yapılmadı.");
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "update" });

		try {
			if (!(await isPictureValid(updateRequest.picture))) {
				message.info(INVALID_PICTURE_MESSAGE);
				return;
			}

			const response = await VolunteersExperienceService.updateVolunteersExperience(updateRequest);

			if (response.isSuccessful) {
				message.success("Gönüllü deneyimi başarıyla güncellendi.");
				clearVolunteersExperienceFormData();
				navigate(VOLUNTEERS_EXPERIENCE_LIST_PATH);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
			if (isNotFoundError(error)) {
				navigate(VOLUNTEERS_EXPERIENCE_LIST_PATH);
			}
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersExperienceEditForm, volunteersExperienceDetail, navigate, clearVolunteersExperienceFormData]);

	const removeVolunteersExperience = useCallback(async (id: string) => {
		if (isMutatingRef.current) {
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await VolunteersExperienceService.removeVolunteersExperience(id);

			if (response.isSuccessful) {
				message.success("Gönüllü deneyimi başarıyla silindi.");
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}

		// A record deleted elsewhere meanwhile, or a failed delete, still leaves the list up to date.
		await getVolunteersExperienceList();
	}, [getVolunteersExperienceList]);

	const contextValue = useMemo<VolunteersExperienceContextState>(
		() => ({
			volunteersExperienceList,
			volunteersExperienceDetail,
			volunteersExperienceForm,
			volunteersExperienceEditForm,
			operationStatus,
			setVolunteersExperienceForm,
			setVolunteersExperienceEditForm,
			clearVolunteersExperienceFormData,
			clearVolunteersExperienceList,
			getVolunteersExperienceList,
			getVolunteersExperienceDetails,
			addVolunteersExperience,
			updateVolunteersExperience,
			removeVolunteersExperience,
		}),
		[
			volunteersExperienceList,
			volunteersExperienceDetail,
			volunteersExperienceForm,
			volunteersExperienceEditForm,
			operationStatus,
			clearVolunteersExperienceFormData,
			clearVolunteersExperienceList,
			getVolunteersExperienceList,
			getVolunteersExperienceDetails,
			addVolunteersExperience,
			updateVolunteersExperience,
			removeVolunteersExperience,
		]
	);

	return <VolunteersExperienceContext.Provider value={contextValue}>{children}</VolunteersExperienceContext.Provider>;
};
